/**
 * Şubeler sayfası — BOSS.
 * CRUD + her şube için QR yazdırma.
 */

import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, RotateCcw, MapPin, Building2, QrCode } from 'lucide-react'
import {
  tumSubeleriGetir,
  subeSil,
  subeGeriYukle,
} from '@/lib/services/sube-service'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { SubeFormModal } from './SubeFormModal'
import { SubeQrModal } from '@/components/qr/SubeQrModal'
import type { Sube } from '@/types'

export function SubelerPage() {
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [pasifGoster, setPasifGoster] = useState(false)

  const [formAcik, setFormAcik] = useState(false)
  const [duzenlenecek, setDuzenlenecek] = useState<Sube | null>(null)
  const [silinecek, setSilinecek] = useState<Sube | null>(null)

  const [qrAcik, setQrAcik] = useState(false)
  const [qrSube, setQrSube] = useState<Sube | null>(null)

  async function yukle() {
    try {
      setYukleniyor(true)
      const data = await tumSubeleriGetir()
      setSubeler(data)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Şubeler yüklenemedi.')
    } finally {
      setYukleniyor(false)
    }
  }

  useEffect(() => {
    yukle()
  }, [])

  async function silOnay() {
    if (!silinecek) return
    try {
      await subeSil(silinecek.id)
      setSilinecek(null)
      await yukle()
    } catch (err) {
      alert('Silinemedi: ' + (err as Error).message)
    }
  }

  async function geriYukleSube(s: Sube) {
    try {
      await subeGeriYukle(s.id)
      await yukle()
    } catch (err) {
      alert('Geri yüklenemedi: ' + (err as Error).message)
    }
  }

  function qrGoster(s: Sube) {
    setQrSube(s)
    setQrAcik(true)
  }

  const gosterilen = pasifGoster ? subeler : subeler.filter((s) => s.aktif)

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Şubeler</h1>
          <p className="text-sm text-gray-600 mt-1">
            {gosterilen.length} {pasifGoster ? 'kayıt' : 'aktif şube'}
            {!pasifGoster && subeler.length > gosterilen.length && (
              <button
                onClick={() => setPasifGoster(true)}
                className="ml-2 text-brand-600 hover:underline"
              >
                ({subeler.length - gosterilen.length} pasif gizli)
              </button>
            )}
            {pasifGoster && (
              <button
                onClick={() => setPasifGoster(false)}
                className="ml-2 text-brand-600 hover:underline"
              >
                (sadece aktifler)
              </button>
            )}
          </p>
        </div>
        <button
          onClick={() => {
            setDuzenlenecek(null)
            setFormAcik(true)
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Yeni Şube</span>
        </button>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 mb-4">
          {hata}
        </div>
      )}

      {yukleniyor && (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      )}

      {!yukleniyor && gosterilen.length === 0 && (
        <div className="card text-center py-12">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">Henüz şube yok</h3>
          <p className="text-gray-600 mb-6">İlk şubeyi eklemek için yukarıdaki butonu kullan.</p>
          <button
            onClick={() => {
              setDuzenlenecek(null)
              setFormAcik(true)
            }}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            İlk Şubeyi Ekle
          </button>
        </div>
      )}

      {!yukleniyor && gosterilen.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {gosterilen.map((s) => {
            const lat = s.konum?.lat
            const lng = s.konum?.lng
            return (
              <div key={s.id} className={`card ${!s.aktif ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h3 className="font-bold text-gray-900 truncate">{s.isim}</h3>
                      {!s.aktif && (
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">Pasif</span>
                      )}
                    </div>
                    {s.adres && (
                      <p className="text-sm text-gray-600 mb-2 truncate">{s.adres}</p>
                    )}
                    {lat !== undefined && lng !== undefined && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        <span>{lat.toFixed(5)}, {lng.toFixed(5)}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col gap-1">
                    {s.aktif ? (
                      <>
                        <button
                          onClick={() => qrGoster(s)}
                          className="p-2 hover:bg-brand-50 rounded-lg text-gray-600 hover:text-brand-600 transition-colors"
                          title="QR Yazdır"
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setDuzenlenecek(s)
                            setFormAcik(true)
                          }}
                          className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 hover:text-gray-900 transition-colors"
                          title="Düzenle"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setSilinecek(s)}
                          className="p-2 hover:bg-red-50 rounded-lg text-gray-600 hover:text-red-600 transition-colors"
                          title="Pasif Yap"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => geriYukleSube(s)}
                        className="p-2 hover:bg-green-50 rounded-lg text-gray-600 hover:text-green-600 transition-colors"
                        title="Geri Yükle"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <SubeFormModal
        acik={formAcik}
        sube={duzenlenecek}
        onKapat={() => setFormAcik(false)}
        onKaydedildi={() => {
          setFormAcik(false)
          yukle()
        }}
      />

      <SubeQrModal
        acik={qrAcik}
        sube={qrSube}
        onKapat={() => setQrAcik(false)}
      />

      <ConfirmDialog
        acik={!!silinecek}
        baslik="Şubeyi Pasif Yap"
        mesaj={`"${silinecek?.isim}" pasif duruma alınacak. Geçmiş kayıtlar korunur. Devam mı?`}
        onayMetni="Pasif Yap"
        tehlikeli
        onOnay={silOnay}
        onIptal={() => setSilinecek(null)}
      />
    </div>
  )
}
